import type { AxiosError } from "axios";
import type { UseQueryResult } from "react-query";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { AnalyticsGA } from "../../utils/AnalyticsGA";
import apiBase from "../../utils/apiBase";
import { formatNumber } from "../../utils/NumbersFormatting";
import type { Message } from "../reactQueryHooks/useRequests";

interface PayloadMessage {
  role: string;
  content: string;
}

interface RequestPayload {
  messages: PayloadMessage[];
  maxTokens: number;
  isStream: boolean;
}

interface ResponseChoice {
  index: number;
  finishReason: string;
  message: PayloadMessage;
}

interface ResponsePayload {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: ResponseChoice[];
}

export interface IDeleteCacheItemRequest {
  organizationId: string;
  cacheItemId: string;
  applicationId: string;
}
export interface IDeleteBulkCacheItemRequest {
  organizationId: string;
  cacheItemsIds: {
    applicationId: string;
    cacheItemId: string;
  }[];
}

export interface IAddCacheItemResponse {
  id: string;
  organizationId: string;
  cacheRedisId: string;
  applicationId: string;
  llm: string;
  path: string;
  requestPayload: RequestPayload;
  responsePayload: ResponsePayload;
}

export interface ICacheItemRequest {
  prompt: string;
  answer: string;
}

export interface ICacheItemResponsePayload {
  prompt: string;
  choices: IChoice[];
}

export interface IAddCacheItemRequest {
  organizationId: string;
  applicationId: string;
  cacheItems: ICacheItemRequest[];
}

export interface IUpdateCacheItemRequest {
  organizationId: string;
  cacheItemId: string;
  cacheItem: { answer: string };
  applicationId: string;
  choiceId: string;
}

interface IRequestParams {
  ApplicationId: string;
}

interface ICacheItem {
  id: string;
  content: string;
  created: string;
  risk: number;
  quality: number;
  hits: number;
  applicationId: string;
}

export interface ICacheItemsRequest {
  PageIndex?: number;
  PageSize?: number;
  Search?: string;
  ApplicationId?: string;
  desc?: boolean;
  orderBy?: string;
}

interface ICacheItemsResponse {
  totalCount: number;
  filteredCount: number;
  pageIndex: number;
  pageSize: number;
  search: string;
  items: ICacheItem[];
  cacheHitRatio?: number;
}

export interface ISimilarPrompt {
  id: string;
  prompt: string;
}
export interface IPromptContext {
  data: {
    query: string;
    context: string;
  };
  type: string;
}

export type IPrompType = "contextual_task" | "rag" | "other";

export interface ICacheItemResponse {
  applicationName: string;
  id: string;
  organizationId: string;
  cacheRedisId: string;
  applicationId: string;
  llm: string;
  path: string;
  requestPayload: {
    messages: Message[];
    max_tokens: number;
    stream: boolean;
  };
  responsePayload: {
    id: string;
    object: string;
    created: number;
    model: string;
    choices: Array<{
      id: string;
      index: number;
      finish_reason: string;
      message: {
        role: string;
        content: string;
      };
    }>;
  };
  similarPrompts: ISimilarPrompt[];
  riskAssessment: {
    id: string;
    score: number;
    scoreByCategories: {
      [key: string]: {
        score: number;
        flag: number;
      };
    };
  };
  state: string;
  promptType: IPrompType;
  promptContext?: IPromptContext;
}

interface IAddSimilarPromptsRequest {
  organizationId: string;
  cacheItemId: string;
  prompts: string[];
  applicationId: string;
}

interface IDeleteSimilarPromptRequest {
  organizationId: string;
  cacheItemId: string;
  similarPromptId: string;
  applicationId: string;
}

interface CacheItemRequest {
  id: string;
  organizationId: string;
  applicationId: string;
  cacheItemId: string;
  similarPromptId: string;
  cacheType: string;
  request: {
    path: string;
    user: {
      id: string;
      encodedIp: string;
    };
    serverIp: string;
    timestamp: string;
    headers: {
      [key: string]: string;
    };
    body: string;
  };
  response: {
    statusCode: number;
    model: string;
    totalTime: number;
    totalTokens: number;
    completionTokens: number;
    promptTokens: number;
    similarityScore: number;
    similarityThreshold: number;
    headers: {
      [key: string]: string;
    };
    body: string;
  };
}

interface CacheItemsRequestResponse {
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  search: string;
  items: CacheItemRequest[];
}

export interface IChoice {
  index: number;
  finish_reason: string;
  message: PayloadMessage;
  id: string;
}

export interface IAddAnswerRequest {
  organizationId: string;
  applicationId: string;
  cacheItemId: string;
  answer: string;
}

export interface IDeleteAnswerRequest {
  organizationId: string;
  applicationId: string;
  cacheItemId: string;
  id: string;
}

export const getPrompt = (requestPayload: ICacheItemResponse): string => {
  const { requestPayload: { messages = [] } = {} } = requestPayload;
  const prompt = messages.filter(message => message.role === "user");
  return prompt[prompt.length - 1]?.content;
};

export const getHistory = (responsePayload: ICacheItemResponse): Message[] => {
  const { requestPayload: { messages = [] } = {} } = responsePayload;
  const history: Message[] = [];
  messages.forEach((message: any, index: number) => {
    if (
      message.role === "system" ||
      (message.role === "user" && index === messages.length - 1)
    ) {
      return;
    }
    history.push(message);
  });

  return history;
};

const deleteCacheItem = async (data: IDeleteCacheItemRequest): Promise<any> => {
  const { cacheItemId, organizationId, applicationId } = data;
  return await apiBase.delete(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}`,
  );
};

const deleteCacheItems = async (
  data: IDeleteBulkCacheItemRequest,
): Promise<any> => {
  const { cacheItemsIds, organizationId } = data;
  return await apiBase.delete(`/organizations/${organizationId}/cache-items`, {
    data: {
      cacheItemsIds: cacheItemsIds,
    },
  });
};

export const addCacheItem = async (
  request: IAddCacheItemRequest,
): Promise<IAddCacheItemResponse[]> => {
  const { organizationId, applicationId, cacheItems } = request;

  return await apiBase.post(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items`,
    {
      cacheItems,
    },
  );
};

export const updateCacheItem = async (
  request: IUpdateCacheItemRequest,
): Promise<any> => {
  const { organizationId, cacheItemId, cacheItem, applicationId, choiceId } =
    request;
  return await apiBase.patch(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}/choices/${choiceId}`,
    {
      cacheItem,
    },
    {
      headers: {
        "X-API-Version": "2.0",
      },
    },
  );
};

export const addAnswer = async (req: IAddAnswerRequest): Promise<IChoice> => {
  const { organizationId, applicationId, cacheItemId, answer } = req;
  return await apiBase.post(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}/choices`,
    { answer },
    {
      headers: {
        "X-API-Version": "2.0",
      },
    },
  );
};

export const deleteAnswer = async (req: IDeleteAnswerRequest): Promise<any> => {
  const { organizationId, applicationId, cacheItemId, id } = req;
  return await apiBase.delete(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}/choices/${id}`,
    {
      headers: {
        "X-API-Version": "2.0",
      },
    },
  );
};

export const fetchCacheItem = async (
  cacheItemId: string,
  organizationId: string,
  applicationId: string,
): Promise<ICacheItemResponse | null> => {
  return await apiBase.get(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}`,
  );
};

export const fetchCacheItemsCount = async (
  organizationId: string,
  applicationId: string,
): Promise<number> => {
  const count: number = await apiBase.get(
    `/organizations/${organizationId}/cache-items/count`,
    {
      params: {
        applicationId,
      },
    },
  );

  return count || 0;
};

export const fetchCacheItems = async (
  organizationId: string,
  params: ICacheItemsRequest,
): Promise<ICacheItemsResponse> => {
  const { PageIndex, Search, ApplicationId, orderBy, desc } = params;

  const getParams: any = {};

  getParams.orderBy = orderBy;
  getParams.desc = desc;

  if (PageIndex) {
    getParams.pageIndex = PageIndex - 1;
  }
  if (Search) {
    getParams.search = Search;
  }

  if (ApplicationId) {
    getParams.applicationId = ApplicationId;
  }

  const response: ICacheItemsResponse = await apiBase.get(
    `/organizations/${organizationId}/cache-items`,
    {
      params: getParams,
    },
  );

  return response;
};

const addSimilarPrompts = async ({
  organizationId,
  cacheItemId,
  prompts,
  applicationId,
}: IAddSimilarPromptsRequest): Promise<ISimilarPrompt[]> => {
  return await apiBase.post(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}/similar-prompts`,
    {
      prompt: prompts,
    },
  );
};

const deleteSimilarPrompt = async ({
  organizationId,
  cacheItemId,
  similarPromptId,
  applicationId,
}: IDeleteSimilarPromptRequest): Promise<ICacheItemResponse> => {
  return await apiBase.delete(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}/similar-prompts/${similarPromptId}`,
  );
};

export const exportCacheItems = async (
  organizationId: string,
  request: IRequestParams,
): Promise<string> => {
  return await apiBase.get(
    `/organizations/${organizationId}/cache-items/export`,
    {
      params: {
        ApplicationId: request.ApplicationId,
      },
    },
  );
};

interface fetchCacheItemRequestsParams {
  pageIndex?: number;
}

export const fetchCacheItemRequests = async (
  cacheItemId: string,
  organizationId: string,
  applicationId: string,
  pageIndex: number,
): Promise<ICacheItemResponse | null> => {
  const params: fetchCacheItemRequestsParams = {};
  if (pageIndex) {
    params.pageIndex = pageIndex - 1;
  }

  return await apiBase.get(
    `/organizations/${organizationId}/applications/${applicationId}/cache-items/${cacheItemId}/requests`,
    {
      params,
    },
  );
};

export const useCacheItems = (
  organizationId: string,
  request: ICacheItemsRequest,
): UseQueryResult<ICacheItemsResponse, unknown> => {
  return useQuery(
    [
      "cacheItems",
      organizationId,
      request.PageIndex,
      request.Search,
      request.ApplicationId,
      request.orderBy,
      request.desc,
    ],
    () => fetchCacheItems(organizationId, request),
    {
      enabled: !!organizationId,
    },
  );
};

export const useDeleteCacheItem = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteCacheItem, {
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          predicate: function (query: any) {
            if (query.queryKey[0] === "cacheItems") {
              if (
                query.state.data.items.length === 1 &&
                query.state.data.pageIndex > 0
              ) {
                return false;
              }
            }
            return query.queryKey[0] === "cacheItems";
          },
        })
        .catch(console.error);
      queryClient.invalidateQueries("metrics").catch(console.error);
      toast.success("Cache item deleted successfully");
      AnalyticsGA.trackEvent("Cache Item", "Delete", "Cache Item Deleted");
    },
  });
};

export const useDeleteCacheItems = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteCacheItems, {
    onSuccess: data => {
      queryClient
        .invalidateQueries({
          predicate: function (query: any) {
            if (query.queryKey[0] === "cacheItems") {
              if (
                query.state.data.items.length === data.removed?.length &&
                query.state.data.pageIndex > 0
              ) {
                return false;
              }
            }
            return query.queryKey[0] === "cacheItems";
          },
        })
        .catch(console.error);
      queryClient.invalidateQueries("metrics").catch(console.error);

      if (!data.removed?.length) {
        toast.warning("No cache items were deleted. Please try again later");
        return;
      }
      const toastMessage =
        data.removed?.length === 1
          ? "Cache item deleted successfully"
          : `${formatNumber(
              data.removed?.length,
            )} cache items deleted successfully`;
      toast.success(toastMessage);

      AnalyticsGA.trackEvent("Cache Item", "Delete", toastMessage);
    },
  });
};

export const useAddCacheItem = () => {
  const queryClient = useQueryClient();

  return useMutation(addCacheItem, {
    onSuccess: async data => {
      if (data && !data.length) {
        return;
      }
      console.log("add cache item success");
      queryClient.invalidateQueries("cacheItems").catch(console.error);
      queryClient.invalidateQueries("metrics").catch(console.error);
      AnalyticsGA.trackEvent("Cache Item", "Create", "Cache Item Created");
    },
  });
};

export const useCacheItem = (
  cacheItemId: string,
  organizationId: string,
  applicationId: string,
): UseQueryResult<ICacheItemResponse, unknown> => {
  return useQuery(
    ["cacheItem", cacheItemId, organizationId, applicationId],
    () => fetchCacheItem(cacheItemId, organizationId, applicationId),
    {
      enabled: !!cacheItemId && !!organizationId && !!applicationId,
    },
  );
};

export const useDeleteAnswer = () => {
  return useMutation(deleteAnswer, {
    onSuccess: () => {
      toast.success("Answer deleted successfully");
    },
    onError: () => {
      toast.error("Something went wrong. Please try again later.");
    },
  });
};

export const useUpdateCacheItem = () => {
  const queryClient = useQueryClient();

  return useMutation(updateCacheItem, {
    onSuccess: async () => {
      toast.success("Changes saved!");
      queryClient.invalidateQueries("cacheItems").catch(console.error);
    },
    onError: () => {
      toast.error("Something went wrong. Please try again later.");
    },
  });
};

export const useAddAnswer = () => {
  return useMutation(addAnswer, {
    onSuccess: () => {
      toast.success("Changes saved!");
    },
    onError: () => {
      toast.error("Something went wrong. Please try again later.");
    },
  });
};

export const useExportCacheItems = (organizationId: string, params: any) => {
  return useQuery([], () => exportCacheItems(organizationId, params), {
    enabled: false,
  });
};

export const useAddSimilarPrompts = () => {
  return useMutation<ISimilarPrompt[], AxiosError, IAddSimilarPromptsRequest>(
    addSimilarPrompts,
    {
      onSuccess: () => {
        toast.success("Similar prompt added successfully");
        AnalyticsGA.trackEvent(
          "Cache Item",
          "Create similar prompt",
          "Similar Prompt Created",
        );
      },
    },
  );
};

export const useDeleteSimilarPrompt = () => {
  return useMutation<
    ICacheItemResponse,
    AxiosError,
    IDeleteSimilarPromptRequest
  >(deleteSimilarPrompt, {
    onSuccess: () => {
      toast.success("Similar prompt deleted successfully");
      AnalyticsGA.trackEvent(
        "Cache Item",
        "Update similar prompt",
        "Similar Prompt Updated",
      );
    },
  });
};

export const useCacheItemRequests = (
  cacheItemId: string,
  organizationId: string,
  applicationId: string,
  page: number,
): UseQueryResult<CacheItemsRequestResponse, AxiosError> => {
  return useQuery(
    [cacheItemId, organizationId, applicationId, page],
    () =>
      fetchCacheItemRequests(cacheItemId, organizationId, applicationId, page),
    {
      enabled: !!cacheItemId && !!organizationId && !!applicationId,
    },
  );
};

export const useCacheItemsCount = (
  organizationId: string,
  applicationId: string,
): UseQueryResult<number> => {
  return useQuery(
    [organizationId, applicationId],
    () => fetchCacheItemsCount(organizationId, applicationId),
    {
      enabled: !!organizationId && !!applicationId,
    },
  );
};
